import { HcCallType } from "./hc-flow.model";
import { HcLovedOne } from "./hc-loved-one.model";
import { HcResponseMeaning } from "./hc-response-meaning.enum";
import { HcResponseAction } from "./hc-response-actions.enum";
import { HcCallSource } from "./hc-call-source.enum";

export interface HcActiveCall {
  _id: string;
  flowId: string; // Reference to HcFlow._id
  csId: string; // Care seeker ID
  lovedOneId: string; // Reference to HcLovedOne._id
  lovedOne?: HcLovedOne; // Populated on queries

  // Twilio call details
  twilioCallSid: string; // Twilio's unique call identifier
  twilioAccountSid: string; // Twilio account identifier

  // Call configuration snapshot (at time of call)
  callType: HcCallType;
  phoneNumber: string; // Phone number being called
  timezone: string; // Timezone used for scheduling
  callSource?: HcCallSource; // Origin of the call (test-call vs scheduled)

  // Call state tracking
  status?: HcActiveCallStatus; // Optional - defaults to INITIATING
  currentStep?: HcCallStep; // Optional - defaults to INITIAL_GREETING
  stepHistory?: HcCallStepHistory[]; // Optional - defaults to empty array

  // Timing information
  initiatedAt?: Date; // Optional - defaults to now
  firstAttemptAt?: Date; // When the first attempt of this retry chain started (for no-answer retry window)
  acknowledgedAt?: Date; // When user acknowledged the call
  lastWebhookAt?: Date; // Optional - defaults to now
  timeoutAt?: Date; // When the call will timeout

  // Response collection
  collectedResponses?: HcCollectedResponse[]; // Optional - defaults to empty array
  pendingResponses?: HcPendingResponse[]; // Optional - defaults to empty array

  // Twilio webhook state
  webhookCount?: number; // Optional - defaults to 0
  lastWebhookData?: Record<string, unknown>; // Last webhook payload for debugging
  nextTwiMLAction?: string; // Next TwiML action to execute

  // Call configuration
  maxWebhookAttempts?: number; // Optional - defaults to 10
  webhookTimeoutMinutes?: number; // Optional - defaults to 30
  retryAttempt?: number; // Optional - defaults to 0
  /** Links this active call to the single HcHistory for this flow run */
  historyId?: string;
  /** True when call was answered by voicemail/machine and we hung up (treat as not answered) */
  answeredByVoicemail?: boolean;

  /** Lease while SMS timeout handling runs (move to history) — overlapping cron / instances cannot double-complete */
  smsTimeoutProcessingLeaseUntil?: Date;
  smsTimeoutProcessingLeaseOwner?: string;
  /**
   * Voice health-check only: main greeting TwiML was pushed via REST after Twilio status answered/relevant callback.
   * Avoids duplicate Twilio REST updates when multiple status webhooks arrive.
   */
  voiceMainGreetingDelivered?: boolean;

  // Error handling
  errorCount?: number; // Optional - defaults to 0
  lastError?: string; // Last error message
  lastErrorAt?: Date; // When the last error occurred

  // Metadata
  createdAt?: Date; // Optional - defaults to now
  updatedAt?: Date; // Optional - defaults to now
}

export enum HcActiveCallStatus {
  INITIATING = 'HcActiveCallStatus.initiating', // Call is being initiated
  RINGING = 'HcActiveCallStatus.ringing', // Call is ringing
  IN_PROGRESS = 'HcActiveCallStatus.in_progress', // Call is active and waiting for responses
  COLLECTING_RESPONSE = 'HcActiveCallStatus.collecting_response', // Actively collecting user input
  PROCESSING_RESPONSE = 'HcActiveCallStatus.processing_response', // Processing received response
  WAITING_FOR_WEBHOOK = 'HcActiveCallStatus.waiting_for_webhook', // Waiting for Twilio webhook
  COMPLETING = 'HcActiveCallStatus.completing', // Call is being completed
  COMPLETED = 'HcActiveCallStatus.completed', // Call completed successfully
  TIMEOUT = 'HcActiveCallStatus.timeout', // Call timed out
  FAILED = 'HcActiveCallStatus.failed', // Call failed
  CANCELLED = 'HcActiveCallStatus.cancelled' // Call was cancelled
}

export enum HcCallStep {
  INITIAL_GREETING = 'HcCallStep.initial_greeting', // Initial greeting message
  ACKNOWLEDGED = 'HcCallStep.acknowledged', // User has acknowledged the call
  COLLECTING_DTMF = 'HcCallStep.collecting_dtmf', // Collecting DTMF input
  COLLECTING_SPEECH = 'HcCallStep.collecting_speech', // Collecting speech input
  PROCESSING_INPUT = 'HcCallStep.processing_input', // Processing received input
  CONFIRMING_RESPONSE = 'HcCallStep.confirming_response', // Confirming user response
  FOLLOW_UP_QUESTION = 'HcCallStep.follow_up_question', // Asking follow-up questions
  FINAL_MESSAGE = 'HcCallStep.final_message', // Final message before ending
  ENDING_CALL = 'HcCallStep.ending_call' // Ending the call
}

export interface HcCallStepHistory {
  step: HcCallStep;
  startedAt: Date;
  completedAt?: Date;
  duration?: number; // Duration in seconds
  webhookData?: Record<string, unknown>; // Webhook data received during this step
  errorMessage?: string; // Error if step failed
}

export interface HcCollectedResponse {
  responseId: string; // Unique identifier for this response
  responseType: HcActiveCallResponseType;
  responseData: HcActiveCallResponseData;
  collectedAt: Date;
  step: HcCallStep; // Which step collected this response
  webhookData?: Record<string, unknown>; // Original webhook data
  confidence?: number; // Confidence score for speech recognition
  processed: boolean; // Whether this response has been processed
  processedAt?: Date; // When it was processed
  meaning?: HcResponseMeaning; // Interpreted meaning
  action?: HcResponseAction; // Action to take based on response
}

export interface HcPendingResponse {
  responseId: string; // Unique identifier for this pending response
  expectedType: HcActiveCallResponseType; // Expected response type
  timeoutAt: Date; // When this response will timeout
  step: HcCallStep; // Which step is waiting for this response
  retryCount: number; // Number of retries for this response
  maxRetries: number; // Maximum retries allowed
}

export enum HcActiveCallResponseType {
  DTMF_INPUT = 'HcActiveCallResponseType.dtmf_input',
  SPEECH_INPUT = 'HcActiveCallResponseType.speech_input',
  SMS_INPUT = 'HcActiveCallResponseType.sms_input',
  NO_RESPONSE = 'HcActiveCallResponseType.no_response',
  CALL_STATUS_UPDATE = 'HcActiveCallResponseType.call_status_update'
}

export interface HcActiveCallResponseData {
  // For DTMF input
  dtmfSequence?: string;
  dtmfDigit?: string;

  // For speech input
  speechText?: string;
  speechConfidence?: number;
  speechLanguage?: string;

  // For SMS input
  smsText?: string;

  // For call status updates
  callStatus?: string;
  callDuration?: number;
  callDirection?: string;

  // Common fields
  rawData?: Record<string, unknown>; // Raw webhook data
  timestamp?: Date; // When response was received
}

